import type { GridRowParams } from '@mui/x-data-grid'
import type { DateTime } from 'luxon'
import type { FC } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import BidConfirmPriceToolbar from '@/features/bidding/components/price/confirm/BidConfirmPriceToolbar'
import { MAX_PTU_ROWS_PER_DATAGRID } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { useResetPtusContext } from '@/features/bidding/contexts/ResetPtusContext'
import { usePtuExpiration } from '@/features/bidding/hooks/usePtuExpiration'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import getBidConfig from '@/features/bidding/utils/getBidConfig'
import type { AggregatedBidPtu } from '@/features/bidding/utils/model/buildAggregatedBid'
import { buildAggregatedBid } from '@/features/bidding/utils/model/buildAggregatedBid'
import { getPriceBidDataGridColumns } from '@/features/bidding/utils/price/getPriceBiddingDataGridColumns'

type Props = {
  activationGroupBids: ExtendedBid[]
}

const BidConfirmPriceDataGrid: FC<Props> = ({ activationGroupBids }) => {
  const { t, i18n } = useTranslation()
  const { selectedCountry } = useBiddingContext()
  const { isPtuMarkedAsResetForActivationGroup } = useResetPtusContext()
  const { isPtuEditable, getExpirationThreshold, expirationMinutes } = usePtuExpiration()

  const { priceLadderType } = getBidConfig(selectedCountry, activationGroupBids[0].bidType)
  const aggregatedBid = useMemo(
    () => buildAggregatedBid(activationGroupBids, priceLadderType),
    [activationGroupBids, priceLadderType],
  )
  const activationGroupUuids = activationGroupBids.map((bid) => bid.activationGroupUuid)

  // Determine the next PTU that will expire (for row highlighting)
  const expirationThreshold = getExpirationThreshold(
    activationGroupBids[0].bidType,
    activationGroupBids[0].marketProgram,
  )
  let nextPtuToExpire: DateTime | null = null
  if (expirationThreshold) {
    const upcomingPtus = aggregatedBid.ptus
      .filter((p) => p.ptuStart >= expirationThreshold)
      .sort((a, b) => a.ptuStart.toMillis() - b.ptuStart.toMillis())
    if (upcomingPtus.length > 0) {
      const firstUpcoming = upcomingPtus[0]
      const diffMinutes = firstUpcoming.ptuStart.diff(expirationThreshold, 'minutes').minutes ?? 0
      if (diffMinutes <= expirationMinutes) {
        nextPtuToExpire = firstUpcoming.ptuStart
      }
    }
  }

  // Row class logic for styling
  const getRowClassName = (params: GridRowParams) => {
    const ptuRow = params.row as AggregatedBidPtu
    const editable = isPtuEditable(
      ptuRow.ptuStart,
      activationGroupBids[0].bidType,
      activationGroupBids[0].marketProgram,
    )

    // Check if this PTU was reset due to gate closure
    const wasReset = activationGroupUuids.some((ag) => isPtuMarkedAsResetForActivationGroup(ag, ptuRow.ptuStart))
    if (wasReset) return 'ptu-reset-row'

    if (!editable) return 'ptu-disabled-row'

    if (nextPtuToExpire && ptuRow.ptuStart.equals(nextPtuToExpire)) return 'ptu-about-to-be-disabled-row'

    return ''
  }

  const dataGridStyles = {
    '& .ptu-reset-row': {
      backgroundColor: '#ffebee !important', // light red
      '&:hover': { backgroundColor: '#ffcdd2 !important' },
    },
    '& .ptu-disabled-row': {
      backgroundColor: '#f5f5f5 !important',
      cursor: 'not-allowed',
      '&:hover': { backgroundColor: '#f5f5f5 !important' },
    },
    '& .ptu-about-to-be-disabled-row': {
      backgroundColor: '#fff9c4 !important',
      '&:hover': { backgroundColor: '#fff9c4 !important' },
    },
  }

  return (
    <CustomDataGrid
      disableDefaultHeaderBackground
      hideFooter
      columns={getPriceBidDataGridColumns({
        aggregatedBid,
        activationGroupUuids,
        editable: false,
        t,
        locale: i18n.language,
        displayChip: true,
        isPtuEditable,
        isPtuMarkedAsResetForActivationGroup,
        bidType: activationGroupBids[0].bidType,
        marketProgram: activationGroupBids[0].marketProgram,
        nextPtuToExpire,
      })}
      getRowClassName={getRowClassName}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: MAX_PTU_ROWS_PER_DATAGRID,
            page: 0,
          },
        },
      }}
      rows={aggregatedBid.ptus}
      slotProps={{
        toolbar: {
          groupParams: { ...activationGroupBids[0], portfolioCode: activationGroupBids[0].portfolio.code },
        },
      }}
      slots={{ toolbar: BidConfirmPriceToolbar }}
      sx={dataGridStyles}
    />
  )
}

export default BidConfirmPriceDataGrid
